import SortableTable, {TableData} from "@common/SortableTable";
import {VoicemailConfigResponse} from "@/api/dto/connect";
import Button, {StyleType} from "patient-ping-remedy/packages/button";
import Icon, {ICONS} from "patient-ping-remedy/packages/icon";
import {colors} from "patient-ping-remedy/packages/theme";
import Loading from "@common/Loading";
import {
  AttemptBadge,
  AttemptsCell
} from "@/components/pages/settings/telephony/VoicemailConfigsTable.styles";
import {ActionCell} from "@/components/pages/settings/telephony/CampaignConfigTable.styles";
import ContextMenu from "patient-ping-remedy/packages/context-menu";
import IconButton from "patient-ping-remedy/packages/icon-button";
import DisplayHelpers from "@/helpers/display_helpers";
import {useState} from "react";
import Modal from "patient-ping-remedy/packages/modal";
import {StandardSemiBoldTypography, StandardTypography} from "patient-ping-remedy/packages/typography";

type Props = {
    voicemailConfigs: VoicemailConfigResponse[];
    isLoading: boolean;
    error: Error | null;
    onAddClick: () => void;
    onEdit: (response: VoicemailConfigResponse) => void;
    onRemove: (id: number) => void;
}

const TableHeaders = ["Name", "Practice Name", "Callback Number", "Persona", "Voicemail on Attempts", "Used By", ""];

export function VoicemailConfigsTable({voicemailConfigs, isLoading, error, onAddClick, onEdit, onRemove}: Props) {
  const [deleteTarget, setDeleteTarget] = useState<{id: number; name: string} | null>(null);

  const closeDeleteModal = () => setDeleteTarget(null);
  const confirmDelete = () => {
    if (deleteTarget) {
      onRemove(deleteTarget.id);
      closeDeleteModal();
    }
  };

  const tableData: TableData[] = voicemailConfigs.map((config) => ({
    configName: {
      tableHeader: 'Name',
      key: 'name',
      type: 'string',
      value: config.name,
      displayValue: config.name,
      columnWidth: '10%'
    },
    practiceName: {
      tableHeader: 'Practice Name',
      key: 'practiceName',
      type: 'string',
      value: config.practiceName,
      displayValue: config.practiceName,
      columnWidth: '15%'
    },
    callbackNumber: {
      tableHeader: 'Callback Number',
      key: 'callbackNumber',
      type: 'string',
      value: config.callbackNumber,
      displayValue: DisplayHelpers.formatPhoneNumber(config.callbackNumber),
      columnWidth: '15%'
    },
    persona: {
      tableHeader: 'Persona',
      key: 'persone',
      type: 'string',
      value: config.callerPersonaName,
      displayValue: config.callerPersonaName,
      columnWidth: '15%'
    },
    voicemailOnAttempts: {
      tableHeader: 'Voicemail on Attempts',
      key: 'voicemailOnAttempts',
      type: 'array',
      value: config.voicemailOnAttempts,
      displayValue: (
        <AttemptsCell>
          {[...(config.voicemailOnAttempts ?? [])].sort((a, b) => a - b).map((attempt) => (
            <AttemptBadge
              key={attempt}
              backgroundColor={colors.blue1}
              textColor={colors.blue5}
            >
              {attempt}
            </AttemptBadge>
          ))}
        </AttemptsCell>
      ),
      columnWidth: '15%'
    },
    usedBy: {
      tableHeader: 'Used By',
      key: 'usedBy',
      type: 'string',
      value: config.usedBy,
      displayValue: config.usedBy,
      columnWidth: '10%'
    },
    actions: {
      tableHeader: '',
      key: 'actions',
      type: 'custom',
      value: '',
      columnWidth: '5%',
      displayValue: (
        <ActionCell>
          <ContextMenu menuItems={[
            {
              action: () => onEdit(config),
              label: 'Edit'
            },
            {
              action: () => setDeleteTarget({ id: config.id, name: config.name }),
              label: 'Delete'
            }
          ]}
          position="bottom"
          menuTestId="voicemail-config-context-menu"
          triggerTestId="voicemail-config-context-menu">
            <IconButton iconClass={ICONS['ellipsis-h']} styleType={StyleType.TERTIARY} />
          </ContextMenu>
        </ActionCell>
      )
    }
  }));

  return (
    <>
      {isLoading && <Loading/>}
      {(error || voicemailConfigs.length >= 0) && (
        <SortableTable
          id="voicemail-configs-table"
          data={tableData}
          loading={false}
          loadError={error}
          header={TableHeaders}
          sortableColumns={[]}
          searchColumns={['configName', 'callbackNumber']}
          searchPlaceholder={'Search by name or callback number'}
        >
          <Button onClick={onAddClick}>
            <Icon iconClass={ICONS.plus} color={colors.white} style={{ marginRight: '10px' }} />
                      Add Voicemail Config
          </Button>
        </SortableTable>
      )}
      <Modal
        isOpen={!!deleteTarget}
        hideModal={closeDeleteModal}
        headerText="Delete voicemail config"
        buttons={[
          <Button
            key="cancel"
            styleType={StyleType.TERTIARY}
            onClick={closeDeleteModal}
          >
            Cancel
          </Button>,
          <Button
            key="confirm"
            onClick={confirmDelete}
          >
            Delete
          </Button>,
        ]}
      >
        <StandardTypography>
          {'Are you sure you want to delete '}
          <StandardSemiBoldTypography>{deleteTarget?.name}</StandardSemiBoldTypography>
          {'?'}
        </StandardTypography>
      </Modal>
    </>
  );
}
